<script setup lang="ts">
import { createVNode, defineAsyncComponent, ref, render } from "vue";
import MessageBox from "../../../../services/MessageBox";
import { getPermissionList, hasPermission } from "../../../../utils/CommonFunctions";
import { Permission } from "../../../../types/auth";
import {
  FilterField,
  TableAction,
  TableField,
} from "../../../../components/table/table";
import { Item } from "../../../../types/models";
import ListTemplate from "../../../../components/ui/common/ListTemplate.vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import DropdownButton from "../../../../components/ui/common/DropDownButton.vue";
import ItemsForm from "./ItemsForm.vue";
import ItemsImportExcel from "./ItemsImportExcel.vue";
import Buttons from "../../../../components/ui/form/Buttons.vue";
import { tokenManager } from "../../../../api/utils/tokenManager";
import ItemListGridView from "./ItemListGridView.vue";

withDefaults(
  defineProps<{
    showHeader?: boolean;
    showQuickTour?: boolean;
    showTitle?: boolean;
    showNextButton?: boolean;
  }>(),
  {
    showHeader: true,
    showQuickTour: true,
    showTitle: true,
  }
);

defineEmits<{
  (e: "next"): void;
}>();

const endPoint = "/clients/inventory/item-management/items/";
const title = "Items";
const selectedData = ref<any | null>(null);
const showForm = ref(false);
const formMode = ref<
  "create" | "edit" | "view" | "showQuickView" | "toggleStatus"
>("view");
const refreshData = ref(false);

const showImportForm = ref(false)

const fields: TableField[] = [
  { name: "photo", caption: "Photo", sortable: true, fieldType: "image" },
  { name: "code", caption: "Barcode", sortable: true, fieldType: "text" },
  { name: "name", caption: "Name", sortable: true, fieldType: "text" },
  {
    name: "category_names",
    caption: "Categories",
    sortable: true,
    fieldType: "tag",
  },
  { name: "group_names", caption: "Groups", sortable: true, fieldType: "tag" },
  { name: "active", caption: "active", sortable: false, fieldType: "status" },
];

const actions: TableAction[] = [
  {
    name: "add-cart",
    permission: "",
    icon: "ShoppingCart",
    variant: "primary",
    title: "Add Cart",
    visible: () => true,
    ignorePermission: true,
    hideMobile: false,
    hideDesktop: true,
  },
];

const filterFields: FilterField[] = [
  {
    name: "active",
    label: "Status",
    type: "select",
    value: "",
    options: [
      { id: "1", name: "Active" },
      { id: "0", name: "Inactive" },
    ],
  },
  {
    name: "item_types_id",
    label: "Item Type",
    type: "select",
    value: "",
    options: tokenManager.getList("item_type"),
  },
  {
    name: "tracked_by",
    label: "Tracked By",
    type: "select",
    value: "",
    options: [
      { id: "Quantity", name: "Quantity" },
      { id: "Service", name: "Service" },
      { id: "Serial", name: "Serial" },
      { id: "Batch", name: "Batch" },
    ],
  },
  {
    name: "item_groups",
    label: "Item Group",
    type: "select",
    value: "",
    options: tokenManager.getList("item_group"),
  },
  {
    name: "item_categories",
    label: "Item Category",
    type: "select",
    value: "",
    options: tokenManager.getList("item_categories"),
  },
  {
    name: "tax_details_id",
    label: "Tax Definition",
    type: "select",
    value: "",
    options: tokenManager.getList("tax_definition"),
  },
];

const changeMode = (
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus"
) => {
  formMode.value = mode;
  showForm.value = mode === "view" || mode === "edit" || mode === "create";
};

const handleData = (data: any) => {
  selectedData.value = data;
};

const permissions = ref<Permission[]>();
const handlePrmissions = (data: Permission[]) => {
  permissions.value = data;
};

const selectLists = ref([]);
const handleLists = (data: any) => {
  selectLists.value = data;
};

const gridView = ref(false);

const cartItems = ref<Item[]>([]);
const handleTableActions = (action: string, data: Item | any) => {
  if (action == "add-cart") {
    cartItems.value.push(data);
  }
};

//TODO: Move to common functions
const displayModals = (title: string, path: string) => {
  const comp_path = path.replace("/clients", "").replace(/-/g, "");
  const comp =
    (comp_path?.slice(-1) == "/" ? "" : comp_path + "/") +
    title.replace(/ /g, "") +
    (title.slice(-1) == "s" ? "" : "s") +
    "Form.vue";
  const modalComponent = "../../../../features" + comp_path + comp;
  const FormModal = defineAsyncComponent(() =>
    import(/* @vite-ignore */ modalComponent).catch(() => {
      MessageBox.show("Could not load form", "error");
      return null;
    })
  );
  const container = document.createElement("div");
  document.body.appendChild(container);

  let perm: Permission[] = getPermissionList(path.substring(1));
  const vnode = createVNode(FormModal, {
    mode: "create",
    permissions: perm,
    title: title,
    endPoint: path,
    cartItems: cartItems.value,
    listCreateName: "",
    onSave: async (_newRecord: any) => {
      console.log("save clicked");

      // Remove modal
      render(null, container);
      container.remove();
    },
    onClose: () => {
      render(null, container);
      container.remove();
    },
  });

  // Render the modal
  render(vnode, container);
};

</script>

<template>
  <ListTemplate
    :endPoint="endPoint"
    :title="title"
    :fields="fields"
    :actions="actions"
    :filterFields="filterFields"
    :hide-table="gridView"
    :tabList="[]"
    :refreshData="refreshData"
    @mode="changeMode"
    :showForm="showForm"
    @data="handleData"
    @lists="handleLists"
    @permissions="handlePrmissions"
    title-field="name"
    @custom="handleTableActions"
    :show-header="showHeader"
    :show-quick-tour="showQuickTour"
    :show-title="showTitle"
  >
    <template #header-actions>
      <Buttons
        v-if="showNextButton"
        icon="CircleArrowRight"
        caption="Next"
        @click="$emit('next')"
      />
      <Buttons icon="Grid3x3" caption="Grid View" @click="gridView = !gridView" v-if="!gridView"/>
      <Buttons icon="Rows4" caption="List View" @click="gridView = !gridView" v-if="gridView"/>
      <Buttons icon="Import" caption="Import from Excel" @click="showImportForm = true" v-if="hasPermission(permissions, 'create')" />
      <!--<Buttons icon="Photo" caption="Check Photo" @click="handleChckPhoto" v-if="hasPermission(permissions, 'create')" />-->
      <DropdownButton
        key="action-dropdown-sales"
        id="actions-dropdown-sales"
        :count="cartItems.length"
        :is-fab="true"
        :hideArrow="true"
        v-if="cartItems.length > 0"
      >
        <button
          @click="
            displayModals(
              'Sales Quotations',
              '/clients/revenue/sales/sales-quotations/'
            )
          "
          class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <IconComponent name="FileCheck" />
          Create Quotation
        </button>
        <button
          @click="
            displayModals(
              'Sales Invoices',
              '/clients/revenue/sales/sales-invoices/'
            )
          "
          class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <IconComponent name="Receipt" />
          Create Invoice
        </button>
      </DropdownButton>
    </template>    
    <template #form-view>
      <ItemListGridView v-if="gridView" />
      <ItemsForm
        v-if="showForm"
        :permissions="permissions"
        :data="selectedData"
        :end-point="endPoint"
        :lists="selectLists"
        :title="title"
        :mode="formMode"
        @save="refreshData = !refreshData"
        @close="showForm = !showForm"
      />
    </template>
  </ListTemplate>
  <ItemsImportExcel
  v-if="showImportForm"
      :end-point="endPoint"
      @save="refreshData = !refreshData"
      @close="showImportForm = !showImportForm" />
</template>
